import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, PlusCircle, Calculator } from 'lucide-react';
import { type Office, type Department, type Organization } from '@/types/org';
import { type UserProfile } from '@/types/team';
import { type NavItem } from '@/config/navConfig';
import { GamutLogo } from '@/components/ui/GamutLogo';
import { EnvironmentBadge } from '@/components/ui/EnvironmentBadge';
import { SidebarContextSwitcher } from '@/components/layout/SidebarContextSwitcher';
import { UserMenu } from '@/components/layout/UserMenu';
import { hasPermission } from '@/hooks/useRBAC';
import { useJobCostEstimateLaunch } from '@/contexts/JobCostEstimateLaunchContext';
import { profileLinkFor, splitTopBarNav } from '@/utils/topBarNav';

export interface TopBarProps {
    profile: UserProfile | null;
    organization: Organization | null;
    offices: Office[];
    departments: Department[];
    activeOfficeId: string | null;
    activeDepartmentId: string | null;
    setActiveDepartmentId: (id: string | null) => void;
    signOut: () => Promise<void>;
    navGroups: { primary: NavItem[]; organize: NavItem[]; measure: NavItem[]; configure: NavItem[] };
    onJobCreateClick: () => void;
    orgSwitcher?: React.ReactNode;
    adminControl?: React.ReactNode;
}

const isActive = (item: NavItem, pathname: string) =>
    pathname === item.to || (!item.exact && item.to !== '/' && pathname.startsWith(item.to + '/'));

/**
 * WEB-111 increment 1 (Jason, 2026-10-31, PLAN_WEB_JOB_PAGE "Decisions 2026-10-31"): the app shell on md and up is
 * this one 56 px bar, and there is no left rail at any width. Left to right: the logo, the perspective switcher
 * (Global Overview, office, department; it was the rail's block), the org switcher for a multi-org login, the primary
 * destinations as text tabs (icons only below lg so a tablet fits), and More for the rail's other groups (Organize,
 * Measure, Configure) so nothing the rail carried becomes unreachable. Right: the environment badge, the Platform
 * admin chip (WEB-100), the job cost estimate, Create Job inside an office, and the account menu (the rail's user
 * block). Below md the layout keeps its drawer and header (MainLayout).
 */
export const TopBar: React.FC<TopBarProps> = ({
    profile, organization, offices, departments, activeOfficeId, activeDepartmentId, setActiveDepartmentId,
    signOut, navGroups, onJobCreateClick, orgSwitcher, adminControl,
}) => {
    const { pathname } = useLocation();
    const { launcher: openJobCostEstimate } = useJobCostEstimateLaunch();
    // The More menu remembers the path it was opened on, so a navigation closes it without an effect.
    const [moreOpenAt, setMoreOpenAt] = useState<string | null>(null);
    const moreOpen = moreOpenAt === pathname;
    const setMoreOpen = (open: boolean) => setMoreOpenAt(open ? pathname : null);
    const moreRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!moreOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMoreOpenAt(null); };
        const onDown = (e: MouseEvent) => { if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpenAt(null); };
        window.addEventListener('keydown', onKey);
        window.addEventListener('mousedown', onDown);
        return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('mousedown', onDown); };
    }, [moreOpen]);

    const { tabs, more } = splitTopBarNav(navGroups);
    const moreActive = more.some((i) => isActive(i, pathname));
    const home = tabs[0]?.to ?? '/';
    const profileLink = profileLinkFor(activeOfficeId, activeDepartmentId);
    const tab = (active: boolean) =>
        `h-14 flex items-center gap-2 px-2 lg:px-3 text-sm font-semibold no-underline border-b-2 transition-colors ${active ? 'border-accent-electric text-accent-electric' : 'border-transparent text-text-secondary hover:text-text-primary'}`;

    return (
        <header data-testid="top-bar" className="h-14 shrink-0 flex items-center gap-3 px-4 border-b border-divider-subtle bg-bg-primary/60 backdrop-blur-xl z-30">
            <Link to={home} aria-label="Gamut home" className="shrink-0 flex items-center">
                <GamutLogo alt="Gamut" className="w-8 h-8 object-contain" />
            </Link>
            <SidebarContextSwitcher
                variant="bar"
                organization={organization}
                offices={(profile?.role === 'OWNER' || profile?.role === 'ORG_ADMIN') ? offices : offices.filter((o) => o.id === profile?.officeId)}
                departments={departments}
                activeOfficeId={activeOfficeId}
                activeDepartmentId={activeDepartmentId}
                setActiveDepartmentId={setActiveDepartmentId}
                userRole={profile?.role}
                userProfile={profile}
            />
            {orgSwitcher}
            <nav aria-label="Main" className="flex items-stretch min-w-0 overflow-x-auto">
                {tabs.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item, pathname);
                    return (
                        <Link key={item.to} to={item.to} aria-current={active ? 'page' : undefined} className={tab(active)}>
                            <Icon size={16} className="lg:hidden" aria-hidden />
                            <span className="sr-only lg:not-sr-only whitespace-nowrap">{item.label}</span>
                        </Link>
                    );
                })}
                {more.length > 0 && (
                    <div ref={moreRef} className="relative flex">
                        <button type="button" aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen(!moreOpen)} className={`${tab(moreActive)} bg-transparent cursor-pointer`}>
                            More <ChevronDown size={14} aria-hidden />
                        </button>
                        {moreOpen && (
                            <div role="menu" aria-label="More destinations" className="absolute left-0 top-full w-56 z-50 rounded-2xl border border-divider-subtle bg-bg-primary/95 backdrop-blur-xl p-2 shadow-2xl">
                                {more.map((item) => {
                                    const Icon = item.icon;
                                    return (
                                        <Link key={item.to} role="menuitem" to={item.to} aria-current={isActive(item, pathname) ? 'page' : undefined} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-text-primary hover:bg-surface-hover no-underline">
                                            <Icon size={16} aria-hidden /> {item.label}
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </nav>
            <div className="flex-1" />
            <div className="flex items-center gap-2 shrink-0">
                <EnvironmentBadge inline />
                {adminControl}
                {openJobCostEstimate && (
                    <button type="button" onClick={openJobCostEstimate} title="Job cost estimate" aria-label="Open job cost estimate"
                        className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/25">
                        <Calculator size={16} strokeWidth={2.25} />
                    </button>
                )}
                {activeOfficeId && hasPermission(profile, 'CREATE_JOB') && (
                    <button type="button" onClick={onJobCreateClick} aria-label="Create job"
                        className="glass-premium h-9 flex items-center gap-2 px-3 text-text-primary border-none cursor-pointer hover-glow">
                        <PlusCircle size={16} className="text-accent-electric" />
                        <span className="hidden lg:inline font-bold text-sm">Create Job</span>
                    </button>
                )}
                <UserMenu profile={profile} departments={departments} profileLink={profileLink} signOut={signOut} />
            </div>
        </header>
    );
};
